import { useState, useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { selectNewMessageCount, selectIsModalOpened, selectIsScrolled } from 'state/selectors';
import { useModal } from 'useCase/modal/useModal';
import { useChat } from 'useCase/chat/useChat';
import { ModalName } from 'features/Modals';
import { Tooltip } from '@web-chat-core/ui-kit';
import { ChatIcon, ChatLayoutButton, NewMessageBage } from './view';

export const ChatButton = observer(() => {
    const { t } = useTranslation();
    const { toggleModal } = useModal();
    const { resetNewMessageCount, isChatOn } = useChat();

    const isModalOpened = selectIsModalOpened(ModalName.CHAT);
    const newMessageCount = selectNewMessageCount();
    const isChatScrolled = selectIsScrolled();

    const [messageCounter, setMessageCounter] = useState<number>(newMessageCount);

    // Ref для управления анимацией без ререндеров
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleToggleChat = useCallback(() => {
        toggleModal(ModalName.CHAT);
    }, [toggleModal]);

    const handleAnimationEnd = useCallback(() => {
        buttonRef.current?.classList.remove('animating');
    }, []);

    useEffect(() => {
        if (isModalOpened && !isChatScrolled && newMessageCount) {
            resetNewMessageCount();
        }
    }, [isModalOpened, newMessageCount, isChatScrolled]);

    // Эффект для управления анимацией
    useEffect(() => {
        // Запускаем анимацию только если:
        // 1. Чат закрыт
        // 2. Есть новые сообщения
        // 3. Кол-во сообщений увеличилось
        // 4. Анимация еще не идет (проверяем через classList)
        const isMessageCountHasBeenIncreased = newMessageCount > messageCounter;
        if (!isModalOpened && newMessageCount && isMessageCountHasBeenIncreased) {
            // Проверяем, идет ли сейчас анимация
            const isAnimating = buttonRef.current?.classList.contains('animating');

            // Запускаем анимацию только если она не идет
            if (!isAnimating) {
                buttonRef.current?.classList.add('animating');
            }
        }
        setMessageCounter(newMessageCount);
    }, [isModalOpened, newMessageCount, messageCounter]);

    if (!isChatOn()) {
        return null;
    }

    return (
        <Tooltip label={t('web_call_chat')}>
            <ChatLayoutButton
                ref={buttonRef}
                $active={isModalOpened}
                onClick={handleToggleChat}
                onAnimationEnd={handleAnimationEnd}
            >
                <NewMessageBage hasNewMessage={Boolean(newMessageCount)}>
                    <ChatIcon $active={isModalOpened} icon="icMessageChat" size={24} />
                </NewMessageBage>
            </ChatLayoutButton>
        </Tooltip>
    );
});
